import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Sidebar } from "@/components/Sidebar";
import AIChat from "@/components/AIChat";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Box, Plus, Search, AlertTriangle, Package, TrendingDown, Clock,
  MessageSquare, Brain, Bot, ChevronRight, Sparkles, Truck, DollarSign
} from "lucide-react";

export default function PartsPage() {
  const [parts, setParts] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [showAddPart, setShowAddPart] = useState(false);
  const [newPart, setNewPart] = useState({
    part_number: "", name: "", category: "Brakes", price: 0, quantity: 0, min_quantity: 5, supplier: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partsRes, workersRes] = await Promise.all([
        axios.get(`${API}/parts`),
        axios.get(`${API}/digital-workers/parts`)
      ]);
      setParts(partsRes.data);
      setWorkers(workersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPart = async () => {
    try {
      await axios.post(`${API}/parts`, newPart);
      setShowAddPart(false);
      setNewPart({ part_number: "", name: "", category: "Brakes", price: 0, quantity: 0, min_quantity: 5, supplier: "" });
      fetchData();
    } catch (error) {
      console.error("Error adding part:", error);
    }
  };

  const getStockStatus = (part) => {
    if (part.quantity === 0) return { color: "bg-red-500/10 text-red-500 border-red-500/20", label: "Out of Stock" };
    if (part.quantity < part.min_quantity) return { color: "bg-amber-500/10 text-amber-500 border-amber-500/20", label: "Low Stock" };
    return { color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20", label: "In Stock" };
  };

  const lowStockParts = parts.filter(p => p.quantity < p.min_quantity);
  const outOfStockParts = parts.filter(p => p.quantity === 0);
  const backorderedParts = parts.filter(p => p.backorder_status);

  const filteredParts = parts.filter(part => {
    const matchesSearch = part.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         part.part_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterCategory === "all" || part.category === filterCategory;
    return matchesSearch && matchesFilter;
  });

  const categories = [...new Set(parts.map(p => p.category))];

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3" data-testid="parts-title">
              <Box className="w-8 h-8 text-orange-500" />
              Parts
            </h1>
            <p className="text-muted-foreground">AI-powered inventory management and demand forecasting</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowChat(true)} className="gap-2">
              <Brain className="w-4 h-4" /> AI Assistant
            </Button>
            <Dialog open={showAddPart} onOpenChange={setShowAddPart}>
              <DialogTrigger asChild>
                <Button className="btn-ai gap-2" data-testid="add-part-btn">
                  <Plus className="w-4 h-4" /> Add Part
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Part</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Input placeholder="Part Number" value={newPart.part_number} onChange={(e) => setNewPart({...newPart, part_number: e.target.value})} />
                  <Input placeholder="Part Name" value={newPart.name} onChange={(e) => setNewPart({...newPart, name: e.target.value})} />
                  <Select value={newPart.category} onValueChange={(v) => setNewPart({...newPart, category: v})}>
                    <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Brakes">Brakes</SelectItem>
                      <SelectItem value="Filters">Filters</SelectItem>
                      <SelectItem value="Engine">Engine</SelectItem>
                      <SelectItem value="Electrical">Electrical</SelectItem>
                      <SelectItem value="Suspension">Suspension</SelectItem>
                      <SelectItem value="Sensors">Sensors</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-4">
                    <Input type="number" placeholder="Price ($)" value={newPart.price || ""} onChange={(e) => setNewPart({...newPart, price: Number(e.target.value)})} />
                    <Input type="number" placeholder="Quantity" value={newPart.quantity || ""} onChange={(e) => setNewPart({...newPart, quantity: Number(e.target.value)})} />
                  </div>
                  <Input type="number" placeholder="Min Quantity" value={newPart.min_quantity} onChange={(e) => setNewPart({...newPart, min_quantity: Number(e.target.value)})} />
                  <Input placeholder="Supplier" value={newPart.supplier} onChange={(e) => setNewPart({...newPart, supplier: e.target.value})} />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddPart(false)}>Cancel</Button>
                  <Button className="btn-ai" onClick={handleAddPart}>Add Part</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="inventory" className="space-y-6">
          <TabsList>
            <TabsTrigger value="inventory" className="gap-2"><Package className="w-4 h-4" /> Inventory</TabsTrigger>
            <TabsTrigger value="alerts" className="gap-2"><AlertTriangle className="w-4 h-4" /> Alerts</TabsTrigger>
            <TabsTrigger value="workers" className="gap-2"><Bot className="w-4 h-4" /> Digital Workers</TabsTrigger>
          </TabsList>

          {/* Inventory Tab */}
          <TabsContent value="inventory" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Parts", value: parts.length, icon: Package, color: "text-orange-500" },
                { label: "Low Stock", value: lowStockParts.length, icon: TrendingDown, color: "text-amber-500" },
                { label: "Out of Stock", value: outOfStockParts.length, icon: AlertTriangle, color: "text-red-500" },
                { label: "On Backorder", value: backorderedParts.length, icon: Clock, color: "text-blue-500" },
              ].map((stat, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg ${stat.color.replace('text-', 'bg-')}/10 flex items-center justify-center`}>
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search by name or part number..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-48"><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Parts Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Part #</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Name</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Category</th>
                        <th className="text-right p-4 text-sm font-medium text-muted-foreground">Price</th>
                        <th className="text-right p-4 text-sm font-medium text-muted-foreground">Qty</th>
                        <th className="text-center p-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">AI Insight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                      ) : filteredParts.length === 0 ? (
                        <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">No parts found</td></tr>
                      ) : (
                        filteredParts.map((part) => {
                          const status = getStockStatus(part);
                          return (
                            <tr key={part.id} className="border-t border-border hover:bg-muted/30" data-testid={`part-${part.id}`}>
                              <td className="p-4 font-mono text-sm">{part.part_number}</td>
                              <td className="p-4">
                                <div className="font-medium text-foreground">{part.name}</div>
                                {part.supplier && <div className="text-xs text-muted-foreground">{part.supplier}</div>}
                              </td>
                              <td className="p-4"><Badge variant="outline">{part.category}</Badge></td>
                              <td className="p-4 text-right font-medium">${part.price?.toFixed(2)}</td>
                              <td className="p-4 text-right">
                                <span className={part.quantity < part.min_quantity ? "text-red-500 font-bold" : ""}>
                                  {part.quantity}
                                </span>
                                <span className="text-muted-foreground text-xs">/{part.min_quantity}</span>
                              </td>
                              <td className="p-4 text-center">
                                <Badge className={status.color}>{status.label}</Badge>
                                {part.backorder_status && (
                                  <div className="text-xs text-blue-400 mt-1">{part.backorder_status}</div>
                                )}
                              </td>
                              <td className="p-4 max-w-xs">
                                {part.ai_reorder_recommendation && (
                                  <div className="flex items-start gap-1 text-xs text-orange-400">
                                    <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    <span>{part.ai_reorder_recommendation}</span>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="space-y-6">
            {/* Critical Alerts */}
            {outOfStockParts.length > 0 && (
              <Card className="border-red-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-red-500">
                    <AlertTriangle className="w-5 h-5" /> Out of Stock - Immediate Action Required
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {outOfStockParts.map(part => (
                    <div key={part.id} className="p-4 rounded-lg bg-red-500/5 border border-red-500/20 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-foreground">{part.name}</div>
                        <div className="text-sm text-muted-foreground">{part.part_number}</div>
                      </div>
                      {part.ai_reorder_recommendation && (
                        <div className="flex items-center gap-2 text-sm text-red-400">
                          <Sparkles className="w-4 h-4" />
                          {part.ai_reorder_recommendation}
                        </div>
                      )}
                      <Button size="sm" className="btn-ai"><Truck className="w-4 h-4 mr-1" /> Order Now</Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Low Stock Alerts */}
            {lowStockParts.length > 0 && (
              <Card className="border-amber-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-amber-500">
                    <TrendingDown className="w-5 h-5" /> Low Stock Warning
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {lowStockParts.filter(p => p.quantity > 0).map(part => (
                    <div key={part.id} className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-foreground">{part.name}</div>
                        <div className="text-sm text-muted-foreground">{part.part_number} • {part.quantity} remaining</div>
                      </div>
                      {part.ai_reorder_recommendation && (
                        <div className="flex items-center gap-2 text-sm text-amber-400 max-w-md">
                          <Sparkles className="w-4 h-4 flex-shrink-0" />
                          {part.ai_reorder_recommendation}
                        </div>
                      )}
                      <Button size="sm" variant="outline">Reorder</Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Backorder Status */}
            {backorderedParts.length > 0 && (
              <Card className="border-blue-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-blue-500">
                    <Clock className="w-5 h-5" /> Parts on Backorder
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {backorderedParts.map(part => (
                    <div key={part.id} className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-foreground">{part.name}</div>
                        <div className="text-sm text-muted-foreground">{part.part_number}</div>
                      </div>
                      <Badge className="bg-blue-500/10 text-blue-500">{part.backorder_status}</Badge>
                      {part.alternate_part && (
                        <div className="text-sm text-muted-foreground">
                          Alternate: <span className="text-blue-400">{part.alternate_part}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {outOfStockParts.length === 0 && lowStockParts.length === 0 && backorderedParts.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No inventory alerts at this time. All parts are well-stocked!</p>
              </div>
            )}
          </TabsContent>

          {/* Digital Workers Tab */}
          <TabsContent value="workers" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {workers.map((worker) => (
                <Card key={worker.id} className="hover:border-orange-500/30 transition-all group" data-testid={`worker-${worker.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Bot className="w-6 h-6 text-orange-500" />
                      </div>
                      <Badge className="bg-emerald-500/10 text-emerald-500">{worker.status}</Badge>
                    </div>
                    <CardTitle className="text-lg mt-3">{worker.name}</CardTitle>
                    <CardDescription className="text-orange-400">{worker.subtitle}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">{worker.description}</p>
                    
                    <div className="space-y-2">
                      {worker.features?.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <ChevronRight className="w-3 h-3 text-orange-500" />
                          {feature}
                        </div>
                      ))}
                    </div>

                    {worker.metrics && (
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
                        {Object.entries(worker.metrics).map(([key, value]) => (
                          <div key={key} className="text-center">
                            <div className="text-sm font-bold text-foreground">{value}</div>
                            <div className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <AIChat isOpen={showChat} onClose={() => setShowChat(false)} vertical="parts" />
      
      {!showChat && (
        <button onClick={() => setShowChat(true)} className="fixed bottom-6 right-6 w-14 h-14 rounded-full btn-ai flex items-center justify-center shadow-lg hover:scale-110 transition-transform" style={{ zIndex: 9998 }} data-testid="chat-fab">
          <MessageSquare className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
